import { delay } from '../delay';

const TRANSIENT_PATTERNS = [
  /error sending request/i,
  /failed to fetch/i,
  /network ?error/i,
  /load failed/i,
  /\btimeout\b/i,
  /connection (reset|refused|closed|aborted)/i,
];

const PERMANENT_PATTERNS = [
  /access denied/i,
  /unauthorized/i,
  /forbidden/i,
  /not found/i,
  /bad request/i,
  // schema mismatch on response body — ritentare non aiuta
  /error decoding response body/i,
];

function extractMessage(error: unknown): string {
  if (typeof error === 'string') return error;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message ?? '');
  }
  return '';
}

export function isTransientError(error: unknown): boolean {
  const message = extractMessage(error);
  if (!message) return false;
  if (PERMANENT_PATTERNS.some(re => re.test(message))) return false;
  return TRANSIENT_PATTERNS.some(re => re.test(message));
}

export interface RetryOptions {
  maxAttempts?: number;
  baseDelayMs?: number;
  jitter?: boolean;
  isRetryable?: (error: unknown) => boolean;
  onRetry?: (info: { attempt: number; error: unknown; nextDelayMs: number }) => void;
}

export async function retryWithBackoff<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const { maxAttempts = 3, baseDelayMs = 500, jitter = true, isRetryable = isTransientError, onRetry } = options;

  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      const isLastAttempt = attempt === maxAttempts;
      if (isLastAttempt || !isRetryable(error)) throw error;
      const exponential = baseDelayMs * 2 ** (attempt - 1);
      const nextDelayMs = jitter ? exponential + Math.floor(Math.random() * baseDelayMs) : exponential;
      onRetry?.({ attempt, error, nextDelayMs });
      await delay(nextDelayMs);
    }
  }
  throw lastError;
}
